/**
 * Журнал событий по всем доступным машинам.
 *
 * Типы событий переводятся по словарю. Незнакомый тип показывается как есть,
 * а не прячется: неизвестное событие — повод его увидеть, а не скрыть.
 */

import { useEffect, useState } from 'react'

import { ApiError, api, type Device, type EventRow } from './api'
import { dateTime } from './format'
import { eventText, runText, type ScheduleRun } from './eventName'
import { dict, type Lang } from './i18n'
import { title } from './vehicleId'
import { askForNotifications, notificationsAllowed } from './notify'
import { downloadCsv, reportCsv, reportFilename } from './reportExport'

/** Событий за сутки по парку набирается достаточно; больше показывать некуда. */
const WINDOW_H = 24
const MAX_ROWS = 200

type Props = {
  devices: Device[]
  lang: Lang
}

export function Events({ devices, lang }: Props) {
  const t = dict(lang)
  const [rows, setRows] = useState<EventRow[] | null>(null)
  // Сводки расписания живут отдельным списком: у них нет машины, они про парк
  // целиком. Сводятся с событиями только при показе, по времени.
  const [runs, setRuns] = useState<ScheduleRun[]>([])
  const [error, setError] = useState('')
  // Разрешение спрашивается только по нажатию: браузеры отклоняют просьбу,
  // не вызванную действием человека, и второй раз уже не спросят.
  const [notify, setNotify] = useState<'off' | 'on' | 'blocked'>(() =>
    notificationsAllowed() ? 'on' : 'off',
  )

  useEffect(() => {
    if (devices.length === 0) {
      setRows([])
      return
    }
    let cancelled = false
    const until = new Date()
    const since = new Date(until.getTime() - WINDOW_H * 3600_000)

    // Сводка расписания приходит своим запросом и своим отказом: если её нет,
    // события всё равно должны показаться.
    api
      .scheduleRuns(since)
      .then((data) => {
        if (!cancelled) setRuns(data)
      })
      .catch(() => {
        if (!cancelled) setRuns([])
      })

    api
      .report<EventRow[]>('events', devices.map((d) => d.id), since, until)
      .then((data) => {
        if (cancelled) return
        // Свежие сверху: диспетчеру нужно последнее, а не первое.
        const sorted = [...data].sort((a, b) => b.eventTime.localeCompare(a.eventTime))
        setRows(sorted.slice(0, MAX_ROWS))
      })
      .catch((caught) => {
        if (cancelled) return
        setError(caught instanceof ApiError && caught.message ? caught.message : t.networkError)
      })
    return () => {
      cancelled = true
    }
  }, [devices, t.networkError])

  // По госномеру: в списке машин человек видит именно его, и событие
  // «зажигание включено» обязано звать машину так же. Выгрузка в CSV идёт тем
  // же именем — иначе таблицу не сопоставить с экраном.
  const nameById = new Map(devices.map((d) => [d.id, title(d)]))

  if (error) {
    return (
      <div className="login-error" role="alert">
        {error}
      </div>
    )
  }
  if (rows === null) return <p className="hint">{t.loading}</p>
  if (rows.length === 0) return <p className="hint">{t.nothingFound}</p>

  function save() {
    const until = new Date()
    const since = new Date(until.getTime() - WINDOW_H * 3600_000)
    downloadCsv(
      reportFilename('events', since, until),
      reportCsv('events', rows ?? [], { lang, nameById }),
    )
  }

  /**
   * События и сводки расписания в одной ленте, свежее сверху.
   *
   * Сводка не привязана к машине, поэтому это не одна коллекция, а две,
   * сведённые по времени только на показе.
   */
  const merged = [
    ...rows.map((row) => ({ at: row.eventTime, row })),
    ...runs.map((run) => ({ at: run.at, run, kind: 'run' as const })),
  ].sort((a, b) => b.at.localeCompare(a.at))

  return (
    <>
      {notify === 'on' ? (
        <p className="hint">{t.notifyOn}</p>
      ) : notify === 'blocked' ? (
        <p className="hint">{t.notifyBlocked}</p>
      ) : (
        <button
          className="act ghost"
          onClick={async () => setNotify((await askForNotifications()) ? 'on' : 'blocked')}
        >
          {t.notifyEnable}
        </button>
      )}

      <div className="rows">
        {merged.map((item) =>
          'kind' in item ? (
            <div className="rec" key={'run-' + item.run.at}>
              <div className="rec-top">
                <b>{t.scheduleRun}</b>
                <span>{dateTime(item.run.at, lang)}</span>
              </div>
              <div className="rec-sub">{runText(item.run, t)}</div>
            </div>
          ) : (
            <div className="rec" key={item.row.id}>
              <div className="rec-top">
                <b>{nameById.get(item.row.deviceId) ?? `#${item.row.deviceId}`}</b>
                <span>{dateTime(item.row.eventTime, lang)}</span>
              </div>
              <div className="rec-sub">{eventText(item.row, t)}</div>
            </div>
          ),
        )}
      </div>

      <button className="act ghost" onClick={save}>
        {t.exportAction}
      </button>
    </>
  )
}
